import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Activity, Clock, ShieldCheck, ShieldAlert } from "lucide-react";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    } catch { return iso; }
}

const RESULT_STYLE = {
    created:       { label: "created",        color: "text-emerald-400/90" },
    returned:      { label: "returned",       color: "text-[#cfa052]"      },
    expired:       { label: "expired",        color: "text-rose-400/90"    },
    invalid:       { label: "invalid sig",    color: "text-rose-400/90"    },
    "missing-claims": { label: "missing claims", color: "text-amber-400/90" },
    misconfigured: { label: "misconfigured",  color: "text-rose-400/90"    },
};

export default function AdminHandoffHealth() {
    const { admin } = useAdminAuth();
    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;
    return <AdminHandoffHealthInner />;
}

function AdminHandoffHealthInner() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/handoff-health");
            setData(data);
            setError("");
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <PortalLayout>
            <section
                className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32"
                data-testid="admin-handoff-page"
            >
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · handoff</span>
                    <h1
                        className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]"
                        data-testid="admin-handoff-title"
                    >
                        Handoff <span className="italic">diagnostic</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        The Angel Select server&apos;s clock + the last 50 handoff attempts. Use this when a member
                        reports their link doesn&apos;t work — compare the server epoch against the signer&apos;s, and look
                        for <span className="text-[#e8e4db]">expired</span> rows with high <code>stale_by</code>.
                    </p>
                </div>

                <AdminSubNav />

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="admin-handoff-loading">
                        taking the server&apos;s pulse…
                    </div>
                ) : error ? (
                    <div
                        className="rounded border border-rose-500/30 bg-rose-500/5 p-6 font-poetic text-rose-300"
                        data-testid="admin-handoff-error"
                    >
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                            <div
                                className="rounded border border-white/10 bg-white/[0.02] p-5"
                                data-testid="admin-handoff-clock"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-[#cfa052]" />
                                    <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                        Server clock (UTC)
                                    </span>
                                </div>
                                <div className="font-poetic text-[#e8e4db] text-lg">{data.serverTimeUtc}</div>
                                <div className="font-label text-[10px] text-[#6e6a62] mt-2">
                                    epoch <span className="text-[#9a958a]">{data.serverEpoch}</span>
                                </div>
                            </div>

                            <div
                                className="rounded border border-white/10 bg-white/[0.02] p-5"
                                data-testid="admin-handoff-leeway"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Activity className="w-4 h-4 text-[#cfa052]" />
                                    <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                        Clock-skew leeway
                                    </span>
                                </div>
                                <div className="font-poetic text-[#e8e4db] text-lg">{data.leewaySeconds}s</div>
                                <div className="font-label text-[10px] text-[#6e6a62] mt-2">
                                    tokens up to this stale still accepted
                                </div>
                            </div>

                            <div
                                className="rounded border border-white/10 bg-white/[0.02] p-5"
                                data-testid="admin-handoff-config"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    {data.hubSecretConfigured ? (
                                        <ShieldCheck className="w-4 h-4 text-emerald-400/90" />
                                    ) : (
                                        <ShieldAlert className="w-4 h-4 text-rose-400/90" />
                                    )}
                                    <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                        Hub secret
                                    </span>
                                </div>
                                <div className="font-poetic text-[#e8e4db] text-lg">
                                    {data.hubSecretConfigured ? "configured" : "MISSING"}
                                </div>
                                <div className="font-label text-[10px] text-[#6e6a62] mt-2">
                                    HUB_HMAC_SECRET in backend env
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="admin-handoff-summary">
                            {Object.entries(data.summary || {}).map(([k, v]) => {
                                const style = RESULT_STYLE[k] || { label: k, color: "text-[#9a958a]" };
                                return (
                                    <span
                                        key={k}
                                        className="font-label text-[10px] tracking-[0.25em] uppercase border border-white/10 rounded-full px-3 py-1"
                                        data-testid={`admin-handoff-summary-${k}`}
                                    >
                                        <span className={style.color}>{style.label}</span>
                                        <span className="text-[#9a958a]"> · {v}</span>
                                    </span>
                                );
                            })}
                            <button
                                type="button"
                                onClick={load}
                                className="ml-auto font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] hover:text-[#e8e4db] transition-colors"
                                data-testid="admin-handoff-refresh"
                            >
                                refresh
                            </button>
                        </div>

                        {data.last24h?.baseline && (
                            <div
                                className="mb-6 text-[11px] font-poetic text-[#6e6a62]"
                                data-testid="admin-handoff-baseline"
                            >
                                <span className="text-[#9a958a]">Alert baseline (rolling 7-day):</span>{" "}
                                {data.last24h.baseline.days > 0 ? (
                                    <>
                                        avg <span className="text-[#e8e4db]">{data.last24h.baseline.meanFailures}</span> failures/day ·
                                        red threshold <span className="text-[#e8e4db]">&gt;{data.last24h.baseline.redThreshold}</span> failures/24h
                                    </>
                                ) : (
                                    <>cold-start — alerting only when failures &gt; {data.last24h.baseline.redThreshold} in 24h</>
                                )}
                                {" "}· today <span className="text-[#e8e4db]">{data.last24h.failures}</span>/{data.last24h.total}
                            </div>
                        )}

                        {data.events.length === 0 ? (
                            <div className="text-center py-32" data-testid="admin-handoff-empty">
                                <Activity className="w-8 h-8 mx-auto text-[#6e6a62] mb-3" />
                                <p className="font-poetic text-[#9a958a]">No handoff attempts recorded yet.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto" data-testid="admin-handoff-table-wrap">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="border-b border-white/10">
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">When</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Result</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Sub</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Email</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">iat</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">exp</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">stale_by</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3">Note</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5 font-poetic" data-testid="admin-handoff-list">
                                        {data.events.map((row, idx) => {
                                            const style = RESULT_STYLE[row.result] || { label: row.result, color: "text-[#9a958a]" };
                                            // For invalid-signature rows we capture unverified claims for diagnostics.
                                            const sub = row.sub || row.unverifiedSub;
                                            const email = row.email || row.unverifiedEmail;
                                            const iat = row.iat ?? row.unverifiedIat;
                                            const exp = row.exp ?? row.unverifiedExp;
                                            const unverified = !row.sub && (row.unverifiedSub || row.unverifiedEmail || row.unverifiedIat);
                                            return (
                                                <tr
                                                    key={`${row.timestamp}-${idx}`}
                                                    className="text-[#e8e4db]"
                                                    data-testid={`admin-handoff-row-${idx}`}
                                                >
                                                    <td className="py-3 pr-6 text-[#9a958a] whitespace-nowrap">{formatDate(row.timestamp)}</td>
                                                    <td className={`py-3 pr-6 font-label text-[10px] tracking-[0.25em] uppercase ${style.color}`}>
                                                        {style.label}
                                                    </td>
                                                    <td className="py-3 pr-6 text-[#9a958a] break-all max-w-[180px]">
                                                        {sub || "—"}
                                                        {unverified && sub && (
                                                            <span className="block text-[9px] text-[#6e6a62] mt-0.5">unverified</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 pr-6 text-[#9a958a] break-all">{email || "—"}</td>
                                                    <td className="py-3 pr-6 text-[#6e6a62] font-label text-[10px]">{iat ?? "—"}</td>
                                                    <td className="py-3 pr-6 text-[#6e6a62] font-label text-[10px]">{exp ?? "—"}</td>
                                                    <td className="py-3 pr-6 text-[#6e6a62] font-label text-[10px]">
                                                        {row.stale_by != null ? `${row.stale_by}s` : "—"}
                                                    </td>
                                                    <td className="py-3 text-[#6e6a62] break-all">{row.reason || row.note || ""}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </section>
        </PortalLayout>
    );
}
